import { useApi } from "@/hooks";
import { BybMenuItem, BybSelect } from "@Components/common/byb-ds/input/byb-select";
import { BybButtonVR } from "@Components/game/common";
import ContainerWrapper from "@Components/generic/container-wrapper";
import { Actions, AppContext, Events, UnityBridge } from "@Context/common/general/context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import useI18n from "@i18n";
import { CircularProgress, Container, Divider, Stack, Typography, useTheme } from "@mui/material";
import { Api } from "@Shared/schemas";
import { Action, BybcatConfigType, BybcatEventAction, BybcatResultStatus, BybcatResultType, BybcatUserType } from "@Shared/schemas/bybcat/bybcatSchemas";
import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUnmount } from "react-use";

const BybcatResult = () => {
    const { i18n } = useI18n();
    const theme = useTheme();

    const { state, dispatch } = useContext(AppContext);

    const [controlScheme, setControlScheme] = useState("DYNAMIC");
    const [result, setResult] = useState<BybcatResultType | null>(null);
    const resultId = parseInt(useParams().resultId || "") || -1;
    const [userMap, setUserMap] = useState<Record<number, BybcatUserType>>({});
    const [configMap, setConfigMap] = useState<Record<number, BybcatConfigType>>({});
    const [loading, setLoading] = useState(false);
    const hasSyncedHostGenderRef = useRef(false);
    const originalHostGenderRef = useRef<string | null>(null);
    const syncedHostGenderRef = useRef<string | null>(null);
    const hostGenderSyncPromiseRef = useRef<Promise<unknown> | null>(null);

    const { call: getResults, data: results = [], loading: getResultsLoading } = useApi<typeof Api.Bybcat.GetResults>({
        api: Api.Bybcat.GetResults,
        request: { account_id: state.account.id!, result_ids: [resultId] }
    });

    const { call: getUsers, data: users = [], loading: getUsersLoading } = useApi<typeof Api.Bybcat.GetUsers>({
        api: Api.Bybcat.GetUsers,
        request: { account_id: state.account.id! }
    });

    const { call: getConfigs, data: configs = [], loading: getConfigsLoading } = useApi<typeof Api.Bybcat.GetConfigs>({
        api: Api.Bybcat.GetConfigs,
        request: { account_id: state.account.id! }
    });

    const { call: updateResult, loading: updateResultLoading } = useApi<typeof Api.Bybcat.UpdateResults>({
        api: Api.Bybcat.UpdateResults,
        request: {
            action: Action.UPDATE,
            bybcat_result_id: resultId,
            status: BybcatResultStatus.IN_PROGRESS,
            bybcat_config_id: result?.bybcat_config_id || -1,
            bybcat_user_id: result?.bybcat_user_id || -1
        }
    });

    const { call: updateUser } = useApi<typeof Api.Users.UpdateUser>({
        api: Api.Users.UpdateUser,
        request: {} as any,
        onSuccess: () => {
            // Refresh the account info to get the latest user data for Unity bridge
            dispatch({
                type: Actions.UsersActions.GetAccountInfo
            });
        },
    });

    const navigate = (route: string) => {
        Events.app.navigate.emit({ path: route });
    };

    // SMELL: We need all users and configs to build the user and config maps
    useEffect(() => {
        getUsers({ account_id: state.account.id! });
        getConfigs({ account_id: state.account.id! });
        Events.app.bybCatEvent.uniq(handleBybCatEvent);
    }, []);

    useUnmount(() => {
        Events.app.hideHeaderButton.emit();
        Events.app.bybCatEvent.remove(handleBybCatEvent);
   

        // Workaround - Fix missing models bug in Unity by aligning host user gender with the test user gender
        const hostUserId = state.user.dbId;
        const originalHostGender = originalHostGenderRef.current;
        const syncedHostGender = syncedHostGenderRef.current;
        if (!hostUserId || !originalHostGender || !syncedHostGender || originalHostGender === syncedHostGender) return;

        const restoreHostGender = () => updateUser({
            user_id: hostUserId,
            gender: originalHostGender
        }).catch((error) => {
            console.error("Failed to restore host user gender after BYBCAT result", error);
        });

        if (!hostGenderSyncPromiseRef.current) {
            restoreHostGender();
            return;
        }

        hostGenderSyncPromiseRef.current
            .catch(() => undefined)
            .finally(() => {
                restoreHostGender();
            });
    });

    // When the result ID changes, fetch the result from the API
    useEffect(() => {
        if (!resultId || resultId === -1) return;
        getResults({ account_id: state.account.id!, result_ids: [resultId] });
    }, [resultId]);

    // Set the result to the first result in the results array (should only be one)
    useEffect(() => {
        setResult(!results || results.length === 0 ? null : results[0]);
    }, [results]);

    // Merge all loading states into a single loading state
    useEffect(() => {
        setLoading(getResultsLoading || getUsersLoading || getConfigsLoading || updateResultLoading);
    }, [getResultsLoading, getUsersLoading, getConfigsLoading, updateResultLoading]);

    // Build the user map for quick lookup
    useEffect(() => {
        if (users && users.length > 0) {
            setUserMap(users.reduce((acc, user) => {
                acc[user.id] = user;
                return acc;
            }, {} as Record<number, BybcatUserType>));
        }
    }, [users]);

    // Build the config map for quick lookup
    useEffect(() => {
        if (configs && configs.length > 0) {
            setConfigMap(configs.reduce((acc, config) => {
                acc[config.id] = config;
                return acc;
            }, {} as Record<number, BybcatConfigType>));
        }
    }, [configs]);

    // Workaround: mirror test user gender to host user while this page is mounted.
    useEffect(() => {
        if (hasSyncedHostGenderRef.current || !result) return;
        const hostUserId = state.user.dbId;
        const hostGender = state.user.gender;
        const testUserGender = userMap[result.bybcat_user_id]?.gender;
        
        if (!hostUserId || !hostGender || !testUserGender) return;

        hasSyncedHostGenderRef.current = true;
        originalHostGenderRef.current = hostGender;
        syncedHostGenderRef.current = testUserGender;

        if (hostGender === testUserGender) return;
        
        hostGenderSyncPromiseRef.current = updateUser({
            user_id: hostUserId,
            gender: testUserGender,
        }).catch((error: any) => {
            console.error("Failed to sync host user gender for BYBCAT result", error);
        });
    }, [result, state.user.dbId, state.user.gender, updateUser, userMap]);

    // Update the header title with the user's name
    useEffect(() => {

        let title = `${i18n("bybcat.page.title")}`;
        if (result && userMap[result.bybcat_user_id]) {
            title += ` - ${userMap[result.bybcat_user_id].name}`;
        }

        Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: title });
        return () => Events.app.hideHeaderButton.emit();
    }, [result, userMap]);

    // Push the test config to Unity
    const applyConfig = (config: BybcatConfigType) => {

        console.log("Applying config", config);
        const promise = new Promise((resolve, reject) => {
            UnityBridge.dispatchWebEvent({
                type: UnityBridge.OutgoingWebEvents.BybCatEvent,
                payload: { action: BybcatEventAction.SET_SETTINGS, settings: config },
                successCallback: () => { resolve(true); },
                errorCallback: () => { reject(false); }
            });
        });

        return promise;
    };

    // Start the warmup sequence
    const handleWarmup = () => {
        if (!result) return;
        const user = userMap[result.bybcat_user_id];
        if (!user) return;
        const config = configMap[result.bybcat_config_id];
        if (!config) return;

        // Apply config to Unity, then start warmup
        applyConfig(config)
            .then(() => {
                console.log("Starting warmup");
                UnityBridge.dispatchWebEvent({
                    type: UnityBridge.OutgoingWebEvents.BybCatEvent,
                    payload: {
                        action: BybcatEventAction.WARMUP,
                        id: user.name.toLowerCase(),
                        name: user.name,
                        config_id: config.id,
                        user_id: user.id,
                        gender: user.gender,
                        control_scheme: controlScheme
                    }
                });
            });
    };

    // Start the test sequence
    const handleStart = () => {
        if (!result) return;
        const user = userMap[result.bybcat_user_id];
        if (!user) return;
        const config = configMap[result.bybcat_config_id];
        if (!config) return;

        // Apply config to Unity, then update result to IN_PROGRESS, then start game
        applyConfig(config)
            .then(() => {
                console.log("Updating result to IN_PROGRESS");
                updateResult({
                    action: Action.UPDATE,
                    bybcat_result_id: resultId,
                    bybcat_config_id: config.id,
                    bybcat_user_id: user.id,
                    status: BybcatResultStatus.IN_PROGRESS
                }).then(() => {
                    console.log("Starting test");
                    UnityBridge.dispatchWebEvent({
                        type: UnityBridge.OutgoingWebEvents.BybCatEvent,
                        payload: {
                            action: BybcatEventAction.START,
                            id: result.id,
                            config_id: config.id,
                            user_id: user.id,
                            name: user.name,
                            gender: user.gender,
                            control_scheme: controlScheme
                        }
                    });
                });
            });
    };

    // Sent from Unity. When the test is complete, update the result with the results data and set status to COMPLETE
    const handleBybCatEvent = (event: any) => {
        console.log("BybCatEvent", event);
        if (event.action === BybcatEventAction.UPDATE_RESULT) {
            updateResult({
                action: Action.UPDATE,
                bybcat_result_id: parseInt(event.id),
                status: BybcatResultStatus.COMPLETE,
                bybcat_config_id: parseInt(event.config_id),
                bybcat_user_id: parseInt(event.user_id),
                results_data: event.data
            }).then(() => {
                console.log("Result updated");
                setResult(prev => prev ? { ...prev, status: BybcatResultStatus.COMPLETE } : null);
            });
        }
    };

    return (
      <ContainerWrapper id="BYBCAT-RESULT">
        <Container maxWidth="sm" sx={{ padding: theme.spacing(0, 0, 6) }}>
          <Stack spacing={6}>
            <Stack spacing={4}>
              <Typography variant="text4xl700" color="dsBase.v200">
                {i18n("bybcat.page.title")}
              </Typography>
              {loading ? (
                <Stack sx={{ pt: 32 }} justifyContent={"center"} alignItems={"center"}>
                  <CircularProgress />
                </Stack>
              ) : result ? (
                result.status === BybcatResultStatus.SCHEDULED ? (
                  <Stack spacing={4} sx={{ pt: 6 }}>
                    <Stack spacing={1}>
                      <Typography variant="text3xl700" color="dsBase.v200">
                        {userMap[result.bybcat_user_id]?.name}
                      </Typography>
                      <Typography variant="text2xl500" color="dsNeutral.v300">
                        {configMap[result.bybcat_config_id]?.name}
                      </Typography>
                    </Stack>
                    <Typography variant="text2xl400" color="dsBase.v200" sx={{ lineHeight: "2" }}>
                      {i18n("bybcat.page.instructions_2")}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Stack sx={{ width: "100%" }} spacing={2}>
                        <Typography variant="text1xl400" color="dsNeutral.v300">
                          {i18n("bybcat.label.controls")}
                        </Typography>
                        <BybSelect value={controlScheme} onChange={(e) => setControlScheme(e.target.value as string)}>
                          <BybMenuItem value="DYNAMIC">{i18n("bybcat.controls.dynamic")}</BybMenuItem>
                          <BybMenuItem value="RIGHT">{i18n("bybcat.controls.right")}</BybMenuItem>
                          <BybMenuItem value="LEFT">{i18n("bybcat.controls.left")}</BybMenuItem>
                        </BybSelect>
                      </Stack>
                    </Stack>
                    <Divider />
                    <Stack direction="row" spacing={6} justifyContent={"center"}>
                      <BybButtonVR sx={{ width: "200px" }} size="large" color="secondary" onClick={handleWarmup}>
                        {i18n("bybcat.label.warmup")}
                      </BybButtonVR>
                      <BybButtonVR sx={{ width: "200px" }} size="large" color="primary" onClick={handleStart}>
                        {i18n("bybcat.button.start")}
                      </BybButtonVR>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack sx={{ pt: 24 }} spacing={4} justifyContent={"center"} alignItems={"center"}>
                    <Typography variant="text3xl600" color="dsBase.v200" sx={{ textAlign: "center" }}>
                      {i18n("bybcat.page.completed_title")}
                    </Typography>
                    <Typography
                      variant="text2xl400"
                      color="dsNeutral.v300"
                      sx={{
                        textAlign: "center",
                        whiteSpace: "pre-line",
                        lineHeight: "2",
                      }}
                    >
                      {i18n("bybcat.page.completed_body")}
                    </Typography>
                    <BybButtonVR
                      sx={{ width: "200px" }}
                      size="large"
                      color="primary"
                      onClick={() => navigate("/coach/bybcat")}
                    >
                      {i18n("button.Done")}
                    </BybButtonVR>
                  </Stack>
                )
              ) : (
                <Stack sx={{ pt: 32 }} justifyContent={"center"} alignItems={"center"}>
                  <Typography variant="text2xl400" color="dsNeutral.v300">
                    {i18n("bybcat.page.no_results")}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Container>
      </ContainerWrapper>
    );
};

export default BybcatResult;

